<template>
  <div class="w-full h-full p-0 m-0">
    <!-- Upload Area -->
    <div
      v-if="!modelValue?.[props.keyName]"
      class="relative group"
      @dragover.prevent="handleDragOver"
      @dragleave.prevent="handleDragLeave"
      @drop.prevent="handleDrop"
    >
      <label
        for="uploadFile"
        :class="[
          'flex items-center justify-center outline-none rounded-xl cursor-pointer border-2 min-h-48 transition-all duration-300 ease-in-out',
          isDragging
            ? 'border-secondary-400 bg-secondary-50 border-solid scale-105'
            : 'border-gray-300 hover:border-secondary-400 hover:bg-gray-50 border-dashed hover:scale-102',
        ]"
      >
        <div
          :class="[
            'flex w-full h-full p-4 transition-all duration-300',
            layout === 'vertical'
              ? 'flex-col gap-6'
              : 'flex-row gap-4 items-center justify-start',
          ]"
        >
          <!-- Upload Icon -->
          <div class="relative">
            <div
              :class="[
                'rounded-full p-6 transition-all duration-300 w-fit mx-auto',
                isDragging
                  ? 'bg-secondary-100 scale-110'
                  : 'bg-gray-100 group-hover:bg-secondary-100 group-hover:scale-110',
              ]"
            >
              <FileUp
                :size="48"
                :stroke-width="1.5"
                :class="[
                  'transition-colors duration-300',
                  isDragging
                    ? 'text-secondary-600'
                    : 'text-gray-500 group-hover:text-secondary-600',
                ]"
              />
            </div>
          </div>

          <!-- Upload Texts -->
          <div
            :class="[
              'space-y-2 text-gray-700 transition-colors duration-300',
              layout === 'vertical' ? 'text-center' : 'text-left',
            ]"
          >
            <p class="text-lg font-semibold group-hover:text-secondary-600">
              {{ translate('general.upload_logo', pageLang) }}
            </p>
            <p class="text-sm text-gray-500">
              {{ translate('general.upload_logo_desc', pageLang) }}
            </p>
            <p class="text-xs text-gray-400">
              {{ translate('general.upload_logo_desc_2', pageLang) }}
            </p>

            <!-- Upload Button -->
            <div
              :class="[
                'mt-2 px-6 py-2 rounded-lg font-medium transition-all duration-300 inline-block',
                isDragging
                  ? 'bg-secondary-600 text-white scale-105'
                  : 'bg-white border-2 border-gray-300 text-gray-700 group-hover:border-secondary-400 group-hover:text-secondary-600',
              ]"
            >
              {{ translate('general.upload_logo_button', pageLang) }}
            </div>
          </div>
        </div>

        <input
          id="uploadFile"
          type="file"
          accept="image/*"
          class="hidden"
          @change="handleUpload"
        />
      </label>

      <!-- Loading Overlay -->
      <div
        v-if="isUploading"
        class="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center rounded-xl"
      >
        <div class="flex flex-col items-center gap-4">
          <div
            class="w-8 h-8 border-4 border-secondary-600 border-t-transparent rounded-full animate-spin"
          ></div>
          <p class="text-xs text-gray-500">
            {{ translate('general.wait_message', pageLang) }}
          </p>
        </div>
      </div>
    </div>

    <!-- Uploaded Image Display -->
    <div
      v-else
      :class="[
        'w-full flex h-full gap-6 animate-fade-in tems-center',
        layout === 'vertical' ? 'flex-col' : 'flex-row',
      ]"
    >
      <!-- Image Container -->
      <div class="relative group w-full max-w-md">
        <div class="relative overflow-hidden rounded-xl shadow-lg bg-white p-4">
          <img
            :src="modelValue[props.keyName]?.url"
            class="w-full object-contain rounded-lg transition-transform duration-300 group-hover:scale-105"
            :class="props.maxHeight ? props.maxHeight : 'h-64'"
            alt="Uploaded Logo"
          />
        </div>
        <div class="mt-4 text-center">
          <p class="text-sm font-medium text-gray-700">
            {{ translate('general.upload_success', pageLang) }}
          </p>
          <p class="text-xs text-gray-500 mt-1">
            {{ translate('general.upload_change_or_remove', pageLang) }}
          </p>
        </div>
      </div>

      <!-- Action Buttons -->
      <div
        class="flex gap-3"
        :class="layout === 'horizontal' ? 'flex-col justify-center' : ''"
      >
        <VButton
          variant="danger"
          class="flex items-center gap-2 px-4 py-2 text-red-600 rounded-lg font-medium transition-all duration-300 hover:scale-105"
          @click="removeLogo"
        >
          <CircleX :size="18" />
          {{ translate('general.remove_logo', pageLang) }}
        </VButton>

        <label
          for="changeFile"
          class="flex items-center gap-2 px-4 py-2 bg-secondary-50 hover:bg-secondary-100 text-secondary-600 rounded-lg font-medium transition-all duration-300 hover:scale-105 cursor-pointer"
        >
          <FileUp :size="18" />
          {{ translate('general.change_logo', pageLang) }}
        </label>
        <input
          id="changeFile"
          type="file"
          accept="image/*"
          class="hidden"
          @change="handleUpload"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { FileUp, CircleX } from 'lucide-vue-next'
import { translate } from 'src/utils/translation'
import { onImageUpload } from 'src/utils/globalFunctions'

const props = defineProps<{
  modelValue: any
  pageLang?: string
  keyName: string
  layout?: 'vertical' | 'horizontal'
  maxHeight?: string
}>()

const emit = defineEmits(['update:modelValue'])

const isDragging = ref(false)
const isUploading = ref(false)

const handleUpload = async (event: Event) => {
  removeLogo()
  isUploading.value = true
  onImageUpload(event, props.pageLang, (data: any) => {
    emit('update:modelValue', {
      ...props.modelValue,
      [props.keyName]: data,
    })
    isUploading.value = false
  })
}

const removeLogo = () => {
  emit('update:modelValue', {
    ...props.modelValue,
    [props.keyName]: null,
  })
}

const handleDragOver = (event: DragEvent) => {
  event.preventDefault()
  isDragging.value = true
}
const handleDragLeave = (event: DragEvent) => {
  event.preventDefault()
  isDragging.value = false
}
const handleDrop = (event: DragEvent) => {
  event.preventDefault()
  isDragging.value = false
  const files = event.dataTransfer?.files
  if (files && files.length > 0) {
    const mockEvent = { target: { files: files } } as any
    handleUpload(mockEvent)
  }
}

// default to vertical
const layout = props.layout ?? 'vertical'
</script>
